/**
 * Frontend UI renderer for the backend-deferred multiple-choice quiz tool.
 *
 * Unlike FrontendQuizTool.tsx (which uses makeAssistantTool to define the tool
 * schema in the frontend and send it to the backend), this component uses
 * makeAssistantToolUI — it only registers a UI renderer. The tool schema is
 * defined in Python as an ExternalToolset (interactive_ui_toolset.py).
 *
 * The interaction flow is identical: the LLM calls the tool, pydantic-ai
 * defers it, the frontend renders this interactive card, the user clicks
 * an answer, and addResult() sends it back to resume the agent.
 */
import { useState, useMemo } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";

type BackendQuizArgs = {
  question: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  correct_answer: string;
};

const LABELS = ["A", "B", "C", "D"] as const;

const BackendQuizRender: ToolCallMessagePartComponent<BackendQuizArgs, string> = ({
  args,
  result,
  status,
  addResult,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const parsed = useMemo(() => {
    if (args == null) return null;
    if (typeof args.question === "string") return args;
    return null;
  }, [args]);

  const isRunning = status.type === "running";
  const hasAnswered = selectedIndex !== null || result != null;

  if (isRunning || !parsed) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 my-2 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        <span>Generating question...</span>
      </div>
    );
  }

  const options = [parsed.option_a, parsed.option_b, parsed.option_c, parsed.option_d];

  if (result != null) {
    // Use capture group to extract the letter after "selected: " — avoids matching
    // the 'c' in "selected" when using a naive [A-D] regex
    const match = typeof result === "string" ? result.match(/selected: ([A-D])/) : null;
    const selectedLabel = match ? (match[1] ?? match[0]).toUpperCase() : null;
    return (
      <div className="border-2 border-teal-200 rounded-lg p-4 my-2 space-y-3">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">
            Backend Tool
          </span>
        </div>
        <p className="font-medium text-sm">{parsed.question}</p>
        <div className="flex flex-col gap-2">
          {options.map((option: string, i: number) => {
            if (!option) return null;
            const label = LABELS[i];
            const isSelected = selectedLabel === label;
            return (
              <div
                key={label}
                className={cn(
                  "flex items-center text-left text-sm rounded-lg border px-3 py-2.5",
                  isSelected
                    ? "border-teal-500 bg-teal-100 text-teal-800 font-medium"
                    : "border-gray-200 bg-gray-50 text-gray-400"
                )}
              >
                <span className="font-semibold text-teal-600 mr-2.5">{label}.</span>
                {option}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">Answer submitted</p>
      </div>
    );
  }

  const handleSelect = (index: number) => {
    if (hasAnswered) return;
    setSelectedIndex(index);
    addResult(
      `The user selected: ${LABELS[index]}. Evaluate whether this matches correct_answer and respond with text only. Do NOT call any tool.`
    );
  };

  return (
    <div className="border-2 border-teal-200 rounded-lg p-4 my-2 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">
          Backend Tool
        </span>
      </div>
      <p className="font-medium text-sm">{parsed.question}</p>
      <div className="flex flex-col gap-2">
        {options.map((option: string, i: number) => {
          if (!option) return null;
          const label = LABELS[i];
          const isSelected = selectedIndex === i;
          return (
            <button
              key={label}
              type="button"
              disabled={hasAnswered}
              onClick={() => handleSelect(i)}
              className={cn(
                "flex items-center text-left text-sm rounded-lg border px-3 py-2.5 transition-all duration-150",
                isSelected
                  ? "border-teal-500 bg-teal-100 text-teal-800 font-medium"
                  : hasAnswered
                    ? "border-gray-200 bg-gray-50 text-gray-400 cursor-default"
                    : "border-teal-200 bg-teal-50/60 text-gray-800 hover:bg-teal-100 hover:border-teal-300 active:scale-[0.98] cursor-pointer"
              )}
            >
              <span className="font-semibold text-teal-600 mr-2.5">{label}.</span>
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const BackendQuizTool = makeAssistantToolUI<BackendQuizArgs, string>({
  toolName: "ask_backend_multiple_choice",
  render: BackendQuizRender,
});
